/**
 * CustomerDrawer — Task 25 (ZTNA Customers v2)
 *
 * ResizableDrawer para criar ou editar um cliente ZTNA.
 *
 * v2 (Spec §10.1):
 *  - Customer e GLOBAL (sem tenant_id).
 *  - Grants apontam para application_id (que carrega tenant_id + environment).
 *  - Create envia application_ids[] em vez de environments[].
 *  - Edit so atualiza dados basicos (grants sao gerenciados via GrantsModal).
 *
 * Validacoes:
 *  - email formato (inline em onBlur + submit)
 *  - telefone formato BR (mascara automatica)
 *  - >=1 aplicacao selecionada (create)
 *  - expiracao <= hoje + 365 dias
 *  - errors do backend (409 conflito -> "e-mail ja cadastrado"; 403 -> sem acesso a app)
 *
 * UX (redesign 2026-05-29):
 *  - Layout em 3 secoes: Identidade, Acesso, Notas.
 *  - Avatar preview com iniciais (cor deterministica do email).
 *  - Mascara automatica de telefone BR (fixo 10 ou celular 11 digitos).
 *  - Chip do ambiente colorido no app picker (DEV/HML/PRD).
 *  - Estado vazio do app picker estilizado.
 *  - Counter no Textarea + helper "X dias" no DatePicker.
 *  - Validacao inline em onBlur (feedback antes do submit).
 *
 * Gates: useCan('ztna_customer','create'|'update')
 */
import { useEffect, useMemo, useState } from 'react'
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  MultiSelect,
  Paper,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { notifications } from '@mantine/notifications'
import {
  IconAt,
  IconBuilding,
  IconCalendar,
  IconCheck,
  IconNote,
  IconPhone,
  IconPlugConnected,
  IconShieldCheck,
  IconUser,
  IconUserPlus,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { ResizableDrawer } from '@/components/ui/ResizableDrawer'
import { PhoneInputIntl } from '@/components/ui/PhoneInputIntl'
import { useCan } from '@/store/auth'
import {
  ztnaCustomersApi,
  type CreateCustomerBody,
  type UpdateCustomerBody,
  type ZtnaCustomer,
} from '@/api/ztnaCustomers'
import { formatApiError } from '@/api/errors'


// ─── Constants ────────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const NOME_COM_ARROBA = 'Informe o nome da pessoa, não o e-mail.'
const NOTES_MAX = 500

// Paleta deterministica para o avatar gerado a partir das iniciais.
// Mantine v7: tokens do tema (--mantine-color-*).
const AVATAR_PALETTE = [
  'var(--mantine-color-teal-6)',
  'var(--mantine-color-cyan-6)',
  'var(--mantine-color-indigo-6)',
  'var(--mantine-color-violet-6)',
  'var(--mantine-color-pink-6)',
  'var(--mantine-color-orange-6)',
  'var(--mantine-color-lime-7)',
] as const

function initialsFor(name: string, email: string): string {
  const source = name.trim() || email.trim()
  if (!source) return '·'
  const parts = source.split(/[\s@.]+/).filter(Boolean)
  if (parts.length === 0) return source[0]?.toUpperCase() ?? '·'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

function colorFor(seed: string): string {
  if (!seed) return AVATAR_PALETTE[0]!
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]!
}

// Cor por ambiente para o chip no app picker.
function envColor(env: string): string {
  const e = env.toUpperCase()
  if (e === 'PRD' || e === 'PROD' || e === 'PRODUCTION') return 'red'
  if (e === 'HML' || e === 'STG' || e === 'STAGING') return 'yellow'
  if (e === 'DEV' || e === 'QA') return 'teal'
  return 'gray'
}

// ─── Section header ────────────────────────────────────────────────────────────

interface SectionHeaderProps {
  icon: React.ReactNode
  title: string
  description?: string
}

function SectionHeader({ icon, title, description }: SectionHeaderProps) {
  return (
    <Box>
      <Group gap={8} mb={4}>
        <Box
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            borderRadius: 6,
            background: 'var(--mantine-color-default-hover)',
            color: 'var(--mantine-color-dimmed)',
          }}
        >
          {icon}
        </Box>
        <Text
          size="xs"
          fw={700}
          tt="uppercase"
          style={{ letterSpacing: '0.08em' }}
          c="dimmed"
        >
          {title}
        </Text>
      </Group>
      {description && (
        <Text size="xs" c="dimmed" pl={32} mt={2}>
          {description}
        </Text>
      )}
    </Box>
  )
}

// ─── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  /** null → create mode; ZtnaCustomer → edit mode */
  customer: ZtnaCustomer | null
  opened: boolean
  onClose: () => void
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function CustomerDrawer({ customer, opened, onClose }: Props) {
  const can = useCan()
  const queryClient = useQueryClient()
  const isEdit = customer != null

  // ── Grantable apps (somente modo create) ──────────────────────────────────────

  const { data: grantableApps = [], isLoading: appsLoading } = useQuery({
    queryKey: ['ztna', 'grantable-apps'],
    queryFn: () => ztnaCustomersApi.listGrantableApps(),
    enabled: !isEdit && opened,
  })

  // Index apps por id pra mostrar chip do ambiente no preview do MultiSelect.
  const appsById = useMemo(() => {
    const m = new Map<string, (typeof grantableApps)[number]>()
    for (const a of grantableApps) m.set(String(a.id), a)
    return m
  }, [grantableApps])

  // Agrupa apps por "tenant_name / environment" para o MultiSelect agrupado do
  // Mantine (data: { group, items: { value, label } }[]).
  const groupedApps = useMemo(() => {
    const byGroup = new Map<string, { value: string; label: string }[]>()
    for (const a of grantableApps) {
      const group = `${a.tenant_name} / ${a.environment}`
      const item = {
        value: String(a.id),
        label: `${a.display_name} — ${a.hostname}`,
      }
      if (!byGroup.has(group)) byGroup.set(group, [])
      byGroup.get(group)!.push(item)
    }
    return Array.from(byGroup.entries()).map(([group, items]) => ({
      group,
      items,
    }))
  }, [grantableApps])

  // ── Form state ────────────────────────────────────────────────────────────────

  const [fullName, setFullName] = useState(customer?.full_name ?? '')
  const [email, setEmail]       = useState(customer?.email ?? '')
  const [company, setCompany]   = useState(customer?.company ?? '')
  const [phone, setPhone]       = useState(customer?.phone ?? '')
  const [notes, setNotes]       = useState('')   // no notes field in API type; kept for UI
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>([])
  const [expiresAt, setExpiresAt] = useState<Date | null>(null)

  // ── Validation errors ─────────────────────────────────────────────────────────

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Reset form state ao fechar (modo create) — reabrir com outro botao
  // "Novo cliente" nao deve carregar valores do ultimo uso.
  // Em modo edit, useState initializers ja garantem os valores do customer.
  useEffect(() => {
    if (!opened && !isEdit) {
      setFullName('')
      setEmail('')
      setCompany('')
      setPhone('')
      setNotes('')
      setSelectedAppIds([])
      setExpiresAt(null)
      setErrors({})
    }
  }, [opened, isEdit])

  // ── Inline validators (onBlur) ─────────────────────────────────────────────────

  function validateField(field: string): void {
    setErrors(prev => {
      const next = { ...prev }
      delete next[field]
      if (field === 'fullName') {
        if (!fullName.trim()) next.fullName = 'Nome obrigatório'
        else if (fullName.includes('@')) next.fullName = NOME_COM_ARROBA
      }
      if (field === 'email' && !isEdit) {
        if (!email.trim()) next.email = 'E-mail obrigatório'
        else if (!EMAIL_RE.test(email)) next.email = 'E-mail inválido'
      }
      if (field === 'phone') {
        const phoneErr = (phone && !/^\+\d{8,15}$/.test(phone)) ? 'Telefone inválido (use +DDI)' : ''
        if (phoneErr) next.phone = phoneErr
      }
      return next
    })
  }

  function validate(): boolean {
    const next: Record<string, string> = {}
    if (!fullName.trim())                next.fullName = 'Nome obrigatório'
    else if (fullName.includes('@'))     next.fullName = NOME_COM_ARROBA
    if (!isEdit) {
      if (!email.trim())                 next.email = 'E-mail obrigatório'
      else if (!EMAIL_RE.test(email))    next.email = 'E-mail inválido'
      if (selectedAppIds.length === 0)   next.apps = 'Selecione pelo menos 1 aplicação'
    }
    const phoneErr = (phone && !/^\+\d{8,15}$/.test(phone)) ? 'Telefone inválido (use +DDI)' : ''
    if (phoneErr) next.phone = phoneErr
    if (!isEdit) {
      if (!expiresAt) {
        next.expiresAt = 'Data de expiração obrigatória'
      } else {
        const maxDate = dayjs().add(365, 'day').toDate()
        if (expiresAt > maxDate)         next.expiresAt = 'Expiração não pode exceder 1 ano'
        if (expiresAt < new Date())      next.expiresAt = 'Expiração não pode ser no passado'
      }
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  // ── Mutations ──────────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: async () => {
      const body: CreateCustomerBody = {
        email: email.trim(),
        full_name: fullName.trim(),
        company: company.trim() || null,
        // Backend recebe digitos crus — formato BR e responsabilidade da UI.
        phone: phone || null,
        application_ids: selectedAppIds.map(Number),
        // ISO completo com offset UTC — backend Pydantic v2 datetime field
        // parseia date-only como naive (caiu em 500 no E2E 2026-05-28).
        expires_at: dayjs(expiresAt!).utc().toISOString(),
      }
      return ztnaCustomersApi.createCustomer(body)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customers'] })
      notifications.show({
        title: 'Cliente criado',
        message: 'Convite enviado por e-mail.',
        color: 'teal',
      })
      onClose()
    },
    onError: (err: Error & { response?: { data?: { detail?: unknown }; status?: number } }) => {
      if (err.response?.status === 409) {
        setErrors(prev => ({ ...prev, email: 'E-mail já cadastrado' }))
      } else if (err.response?.status === 403) {
        notifications.show({
          title: 'Permissão negada',
          message: 'Você não tem acesso a uma das aplicações selecionadas.',
          color: 'red',
        })
      } else {
        notifications.show({
          title: 'Erro ao criar cliente',
          message: formatApiError(err, 'Não foi possível criar o cliente.'),
          color: 'red',
        })
      }
    },
  })

  const updateMutation = useMutation({
    mutationFn: async () => {
      const body: UpdateCustomerBody = {
        full_name: fullName.trim(),
        company: company.trim() || null,
        phone: phone || null,
      }
      await ztnaCustomersApi.updateCustomer(customer!.id, body)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customers'] })
      notifications.show({
        title: 'Cliente atualizado',
        message: 'Dados salvos com sucesso.',
        color: 'teal',
      })
      onClose()
    },
    onError: (err: Error & { response?: { data?: { detail?: unknown } } }) => {
      notifications.show({
        title: 'Erro ao atualizar cliente',
        message: formatApiError(err, 'A operação falhou.'),
        color: 'red',
      })
    },
  })

  // ── Submission gate ────────────────────────────────────────────────────────────

  const permission = isEdit
    ? can('ztna_customer', 'update')
    : can('ztna_customer', 'create')
  const isPending = createMutation.isPending || updateMutation.isPending

  function handleSubmit() {
    if (!permission) return
    if (!validate()) return
    if (isEdit) updateMutation.mutate()
    else        createMutation.mutate()
  }

  // ── maxDate + helper de dias para o picker ─────────────────────────────────────

  const maxDate = dayjs().add(365, 'day').toDate()
  const daysUntilExpiry = expiresAt
    ? Math.max(0, dayjs(expiresAt).diff(dayjs(), 'day') + 1)
    : null

  // ── Header content (avatar preview + identificacao) ────────────────────────────

  const headerColor = colorFor(email || fullName)
  const initials = initialsFor(fullName, email)

  return (
    <ResizableDrawer
      storageKey="ztna-customer-drawer-width"
      defaultWidth={520}
      minWidth={420}
      opened={opened}
      onClose={onClose}
      position="right"
      withCloseButton
      title={
        <Group gap="sm" wrap="nowrap">
          <ActionIcon
            size={36}
            radius="md"
            variant="light"
            color={isEdit ? 'gray' : 'teal'}
            style={{ pointerEvents: 'none' }}
          >
            {isEdit ? <IconUser size={20} /> : <IconUserPlus size={20} />}
          </ActionIcon>
          <Box>
            <Title order={4} mb={0} style={{ lineHeight: 1.15 }}>
              {isEdit ? 'Editar cliente' : 'Novo cliente ZTNA'}
            </Title>
            <Text size="xs" c="dimmed">
              {isEdit
                ? 'Dados básicos do cliente — grants gerenciados separadamente'
                : 'Conceder acesso externo a aplicações ZTNA específicas'}
            </Text>
          </Box>
        </Group>
      }
    >
      <Stack gap="xl">
        {/* ── Avatar preview ──────────────────────────────────────────────────── */}
        <Paper
          withBorder
          radius="md"
          p="md"
          style={{
            background:
              'linear-gradient(135deg, var(--mantine-color-default) 0%, var(--mantine-color-default-hover) 100%)',
          }}
        >
          <Group gap="md" wrap="nowrap">
            <Avatar
              size={56}
              radius="md"
              style={{
                background: headerColor,
                color: 'white',
                fontWeight: 700,
                fontSize: 18,
                letterSpacing: '0.04em',
                transition: 'background 240ms ease',
              }}
            >
              {initials}
            </Avatar>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} size="sm" truncate>
                {fullName.trim() || (
                  <Text component="span" c="dimmed" fs="italic">
                    Nome do cliente…
                  </Text>
                )}
              </Text>
              <Text size="xs" c="dimmed" truncate ff="monospace">
                {email.trim() || 'email@empresa.com.br'}
              </Text>
              {selectedAppIds.length > 0 && (
                <Group gap={4} mt={6}>
                  <IconShieldCheck size={12} style={{ color: 'var(--mantine-color-teal-6)' }} />
                  <Text size="xs" c="teal" fw={500}>
                    {selectedAppIds.length}{' '}
                    {selectedAppIds.length === 1 ? 'aplicação' : 'aplicações'} • {daysUntilExpiry ? `${daysUntilExpiry}d de acesso` : 'sem prazo'}
                  </Text>
                </Group>
              )}
            </Box>
          </Group>
        </Paper>

        {/* ── Seção 1: Identidade ─────────────────────────────────────────────── */}
        <Box>
          <SectionHeader
            icon={<IconUser size={14} />}
            title="Identidade"
            description="Quem é a pessoa que receberá o convite"
          />
          <Stack gap="sm" mt="sm">
            <TextInput
              label="Nome completo"
              placeholder="João da Silva"
              value={fullName}
              onChange={e => setFullName(e.currentTarget.value)}
              onBlur={() => validateField('fullName')}
              error={errors.fullName}
              withAsterisk
              leftSection={<IconUser size={16} />}
              rightSection={
                fullName.trim() && !errors.fullName ? (
                  <IconCheck
                    size={16}
                    style={{ color: 'var(--mantine-color-teal-6)' }}
                  />
                ) : null
              }
            />

            <TextInput
              label="E-mail"
              placeholder="joao@empresa.com.br"
              value={email}
              disabled={isEdit}
              description={
                isEdit
                  ? 'E-mail não pode ser alterado após cadastro'
                  : 'O convite será enviado para este endereço'
              }
              onChange={e => setEmail(e.currentTarget.value)}
              onBlur={() => validateField('email')}
              error={errors.email}
              withAsterisk={!isEdit}
              ff="monospace"
              leftSection={<IconAt size={16} />}
              rightSection={
                email.trim() && EMAIL_RE.test(email) && !errors.email ? (
                  <IconCheck
                    size={16}
                    style={{ color: 'var(--mantine-color-teal-6)' }}
                  />
                ) : null
              }
            />

            <Group grow gap="sm" align="flex-start">
              <TextInput
                label="Empresa"
                placeholder="Acme Corp"
                value={company}
                onChange={e => setCompany(e.currentTarget.value)}
                leftSection={<IconBuilding size={16} />}
              />

              <PhoneInputIntl
                label="Telefone"
                value={phone}
                onChange={setPhone}
                error={errors.phone}
              />
            </Group>
          </Stack>
        </Box>

        {/* ── Seção 2: Acesso (somente create) ────────────────────────────────── */}
        {!isEdit && (
          <>
            <Divider />
            <Box>
              <SectionHeader
                icon={<IconPlugConnected size={14} />}
                title="Acesso"
                description="Aplicações concedidas e prazo de validade"
              />
              <Stack gap="sm" mt="sm">
                <MultiSelect
                  label="Aplicações"
                  description={
                    appsLoading
                      ? 'Carregando aplicações disponíveis…'
                      : grantableApps.length === 0
                        ? 'Nenhuma aplicação grantável disponível no seu escopo'
                        : `${grantableApps.length} ${grantableApps.length === 1 ? 'aplicação disponível' : 'aplicações disponíveis'} — selecione uma ou mais`
                  }
                  data={groupedApps}
                  value={selectedAppIds}
                  onChange={ids => {
                    setSelectedAppIds(ids)
                    if (ids.length > 0 && errors.apps) {
                      setErrors(prev => {
                        const n = { ...prev }
                        delete n.apps
                        return n
                      })
                    }
                  }}
                  error={errors.apps}
                  withAsterisk
                  clearable
                  searchable
                  placeholder={
                    selectedAppIds.length === 0
                      ? 'Selecione as aplicações…'
                      : ''
                  }
                  nothingFoundMessage={
                    <Group gap={8} p={8} c="dimmed">
                      <IconPlugConnected size={16} />
                      <Text size="sm">Nenhuma aplicação encontrada</Text>
                    </Group>
                  }
                  leftSection={<IconPlugConnected size={16} />}
                  maxDropdownHeight={280}
                  hidePickedOptions
                />

                {/* Chips do que foi selecionado, coloridos por ambiente */}
                {selectedAppIds.length > 0 && (
                  <Paper
                    withBorder
                    radius="sm"
                    p="xs"
                    bg="var(--mantine-color-default)"
                  >
                    <Text size="xs" c="dimmed" mb={6} fw={500}>
                      {selectedAppIds.length} {selectedAppIds.length === 1 ? 'aplicação selecionada' : 'aplicações selecionadas'}
                    </Text>
                    <Group gap={6}>
                      {selectedAppIds.map(id => {
                        const app = appsById.get(id)
                        if (!app) return null
                        return (
                          <Badge
                            key={id}
                            size="sm"
                            variant="light"
                            color={envColor(app.environment)}
                            radius="sm"
                            style={{ textTransform: 'none', fontWeight: 500 }}
                          >
                            {app.display_name}
                            <Text component="span" size="xs" c="dimmed" ml={4}>
                              · {app.environment}
                            </Text>
                          </Badge>
                        )
                      })}
                    </Group>
                  </Paper>
                )}

                <DatePickerInput
                  label="Expiração do acesso"
                  description={
                    daysUntilExpiry !== null && !errors.expiresAt
                      ? `${daysUntilExpiry} dias a partir de hoje (máximo 365)`
                      : 'Máximo 1 ano a partir de hoje'
                  }
                  placeholder="Selecione a data"
                  value={expiresAt}
                  onChange={d => {
                    setExpiresAt(d)
                    if (d && errors.expiresAt) {
                      setErrors(prev => {
                        const n = { ...prev }
                        delete n.expiresAt
                        return n
                      })
                    }
                  }}
                  maxDate={maxDate}
                  minDate={new Date()}
                  leftSection={<IconCalendar size={16} />}
                  error={errors.expiresAt}
                  valueFormat="DD/MM/YYYY"
                  withAsterisk
                />
              </Stack>
            </Box>
          </>
        )}

        {/* ── Seção 3: Notas ──────────────────────────────────────────────────── */}
        <Divider />
        <Box>
          <SectionHeader
            icon={<IconNote size={14} />}
            title="Observações"
            description="Notas internas (não enviadas ao cliente)"
          />
          <Textarea
            mt="sm"
            placeholder="Ex.: Cliente do projeto X, contato via Ana do comercial…"
            value={notes}
            onChange={e => setNotes(e.currentTarget.value.slice(0, NOTES_MAX))}
            autosize
            minRows={2}
            maxRows={6}
            description={`${notes.length} / ${NOTES_MAX}`}
            inputWrapperOrder={['label', 'input', 'description', 'error']}
            styles={{
              description: {
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
                color:
                  notes.length > NOTES_MAX * 0.9
                    ? 'var(--mantine-color-orange-6)'
                    : undefined,
              },
            }}
          />
        </Box>

        {!permission && (
          <Paper p="xs" withBorder radius="sm" bg="var(--mantine-color-red-light)">
            <Text size="xs" c="red" fw={500}>
              Sem permissão para {isEdit ? 'editar' : 'criar'} clientes ZTNA.
            </Text>
          </Paper>
        )}

        <Group justify="flex-end" mt="sm" gap="xs">
          <Button variant="subtle" color="gray" onClick={onClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            color="teal"
            onClick={handleSubmit}
            loading={isPending}
            disabled={!permission}
            leftSection={isEdit ? <IconCheck size={16} /> : <IconUserPlus size={16} />}
          >
            {isEdit ? 'Salvar alterações' : 'Criar e enviar convite'}
          </Button>
        </Group>
      </Stack>
    </ResizableDrawer>
  )
}

export default CustomerDrawer
